/**
 * Base API client — all fetch calls go through here.
 * Attaches X-API-Secret header on every request.
 * Attaches Authorization: Bearer <jwt> when authenticated via Supabase Auth.
 * Keeps X-Admin-Token as fallback during auth migration.
 * Throws with error body text on non-OK responses.
 */
import { getAccessToken, authReady } from "../context/AuthContext.jsx";
import { clearAllClientAuthSync } from "../lib/authCleanup.js";

export const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
const API_SECRET = import.meta.env.VITE_API_SECRET ?? "";

export async function apiFetch(path, options = {}) {
  // Hotfix v1.27.2 — wait for Supabase to finish its initial session
  // restore before reading the token. Without this, a useEffect that
  // fires getXpState()/getCandidates() on mount (both fire when `voter`
  // is rehydrated from localStorage) can beat `supabase.auth.getSession()`
  // to the punch, see a null _currentAccessToken, send the request with
  // NO Authorization header, and trigger a 401 that the apiFetch 401
  // handler below then converts into a full clearAllClientAuthSync() —
  // wiping the actually-valid session the user still has. Net result:
  // silent logout on page reload.
  //
  // authReady resolves as soon as either supabase.auth.getSession() or
  // the first onAuthStateChange event returns. When the session is
  // already populated (warm cache, subsequent apiFetch) it's a single
  // microtask. On first call after a reload it's bounded by the
  // localStorage read (sub-10ms in practice) with a 5s safety timeout
  // in AuthContext so a wedged SDK never hangs the whole UI.
  await authReady;

  const headers = {
    "content-type": "application/json",
    "X-API-Secret": API_SECRET,
    ...(options.headers ?? {}),
  };

  // Attach JWT if authenticated (primary auth path)
  const jwt = getAccessToken();
  if (jwt) {
    headers["Authorization"] = `Bearer ${jwt}`;
  }

  // Legacy fallback: attach admin token from sessionStorage
  // TODO: remove after AUTH-5 migration is complete
  const adminToken = sessionStorage.getItem("qm_admin_token");
  if (adminToken) {
    headers["X-Admin-Token"] = adminToken;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  // Global auth failure handler:
  // - 401: always means auth failure → clear session and redirect
  // - 403 with active JWT: token expired or role revoked → clear session and redirect
  // - 403 without JWT on an /admin/* path: unauthenticated admin request →
  //   bounce to /admin?next=<path> (AUTH-16). Not in any app state — e.g.
  //   deep-link paste into an incognito tab.
  // - 403 without JWT elsewhere: legitimate authorization error (e.g.
  //   "admin access required" banner in a voter-side form) → throw normally
  const onAdminPath =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/admin");
  const onManagePath =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/manage");
  const forceAdminLogin = res.status === 403 && !jwt && onAdminPath;

  if (res.status === 401 || (res.status === 403 && jwt) || forceAdminLogin) {
    // Detect user role BEFORE clearing sessionStorage so we redirect to the
    // right login. Path-aware fallback: if the sessionStorage flag has
    // already been cleared (race on rapid 401s, or deep-link), use the URL
    // path as the tiebreaker so /admin/* still ends up on /admin.
    const isAdmin = !!sessionStorage.getItem("qm_admin") || onAdminPath;
    const isPO = !!sessionStorage.getItem("qm_po") || onManagePath;
    // Capture the current path BEFORE the redirect so ?next= survives the
    // session cleanup. Include search+hash so a deep-linked admin tab (e.g.
    // /admin/personas?page=3) returns to the same scroll position.
    //
    // Hotfix v1.27.3 — strip any existing `next` param from the current URL
    // BEFORE re-encoding it as the new ?next=. Without this, each 401/403
    // bounce during an admin login loop appended another layer of
    // encoding: /admin?next=%2Fadmin → /admin?next=%2Fadmin%3Fnext%3D%252Fadmin
    // → /admin?next=%2Fadmin%3Fnext%3D%252Fadmin%26next%3D%25252Fadmin …
    // The URL grew exponentially on every retry, quickly blew past the
    // `current !== target` guard (because the URLs genuinely differed
    // each time), and could eventually exceed browser URL length limits.
    // Now we always rebuild `next` from the *canonical* current path with
    // any prior ?next= removed, so the URL is idempotent across retries.
    const nextParam = (() => {
      if (typeof window === "undefined" || window.location.pathname === "/") return "";
      const search = new URLSearchParams(window.location.search);
      search.delete("next");
      const cleanSearch = search.toString();
      const canonical =
        window.location.pathname +
        (cleanSearch ? `?${cleanSearch}` : "") +
        window.location.hash;
      return `?next=${encodeURIComponent(canonical)}`;
    })();
    // Clear every piece of client-side auth state. Before v1.26.4 this was
    // inlined and missed qm_voter — on the forced redirect below the next
    // page load read qm_voter back, routed to the directory, re-ran the same
    // request, got another 401, and looped. clearAllClientAuthSync() is the
    // single source of truth shared with the explicit logout paths; keeping
    // them in sync means fixing any one logout bug fixes all four callers.
    //
    // We use the SYNC variant because we're about to window.location.href
    // redirect — awaiting supabase.auth.signOut() just delays the bounce
    // while the SDK RPCs a revocation that the fresh page load would redo
    // anyway. localStorage purge is what actually prevents the loop.
    clearAllClientAuthSync();
    // Redirect to the correct login page based on prior role. Preserve the
    // original destination via ?next= so login-success can bounce back.
    if (typeof window !== "undefined") {
      const base = isAdmin ? "/admin" : isPO ? "/manage" : "/";
      // Only attach ?next= when the role has a login screen that honours it.
      const target = isAdmin || isPO ? `${base}${nextParam}` : base;
      // Copilot suppressed #4 — guard against redirecting to the URL we're
      // already on. If a background request on the base page (e.g. an
      // onboarding /api/candidates that 401s before the v1.26.4 anonymous
      // branch landed) kept assigning window.location.href to "/", each
      // assignment restarted navigation and the fetch fired again on the
      // fresh mount — an infinite hard-reload storm. Comparing the full
      // current URL (pathname + search + hash) to the computed target
      // means a 401 at the destination page becomes a thrown error only
      // (caught upstream, UI shows the right state), not a reload.
      //
      // Hotfix v1.27.3 — compare in CANONICAL form (same ?next=-stripped
      // shape the target uses). Before this change, if the current URL
      // already had ?next=/admin on it, the raw `current` string would
      // never match the `target` we just built (which also has ?next=
      // but with a freshly-encoded version), so the guard never tripped
      // and we'd keep redirecting. Canonicalising both sides closes that.
      const currentSearch = new URLSearchParams(window.location.search);
      currentSearch.delete("next");
      const currentClean = currentSearch.toString();
      const current =
        window.location.pathname +
        (currentClean ? `?${currentClean}` : "") +
        window.location.hash;
      const targetPath = target.split("?")[0];
      if (current !== targetPath && current !== target) {
        window.location.href = target;
      }
    }
    throw new Error("Session expired. Please sign in again.");
  }

  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
